import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {ApiError, errorMessage} from './api';
import {useAuth} from './auth';
import {Empty, Notice, Spinner} from './components';
import {AuthorBoundary} from './components/AuthorBoundary';
import {readOnlyReason, useCapacity} from './components/Capacity';
import {CopyCode} from './components/CopyCode';
import {Icon} from './components/Icon';
import {IdentityCell} from './components/IdentityCell';
import {PermissionTable} from './components/PermissionTable';
import type {Catalogue, ProfileHead, ProfileReceipt, ProfileVersion, StaffAccess} from './types';
import type {Identity} from './utils';
import {
  cachedIdentity,
  catalogueRows,
  countSensitive,
  formatDate,
  permissionDiff,
  permissionSetRows,
  unresolvedIdentity,
} from './utils';

type Creation = {mode: 'profile'} | {mode: 'version'; head: ProfileHead; base: string[]};

export function ProfilesPage() {
  const {api, gate, dataEpoch, me, refreshAccess} = useAuth();
  const [profiles, setProfiles] = useState<ProfileHead[]>([]);
  const [nextProfileCursor, setNextProfileCursor] = useState<string | null>(null);
  const [catalogue, setCatalogue] = useState<Catalogue | null>(null);
  const [selected, setSelected] = useState<ProfileHead | null>(null);
  const [versions, setVersions] = useState<ProfileVersion[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState<Creation | null>(null);
  const [users, setUsers] = useState<Array<StaffAccess & {id: string}> | null>(null);
  const capacity = useCapacity();

  const load = useCallback(async () => {
    if (!api) return;
    const snap = gate.snapshot(); setListLoading(true); setError('');
    try {
      const [profilePage, catalogueResult] = await Promise.all([api.profiles(undefined, snap.signal), api.catalogue(snap.signal)]);
      if (!gate.accepts(snap.generation)) return;
      setProfiles(profilePage.items); setNextProfileCursor(profilePage.nextCursor); setCatalogue(catalogueResult);
      setSelected(current => current ? profilePage.items.find(profile => profile.profileId === current.profileId) || current : null);
    } catch (caught) {
      if (!gate.accepts(snap.generation)) return;
      setError(errorMessage(caught));
      if (caught instanceof ApiError && [401, 403].includes(caught.status)) void refreshAccess();
    } finally { if (gate.accepts(snap.generation)) setListLoading(false); }
  }, [api, gate, refreshAccess]);

  /**
   * Le décompte des comptes exige de parcourir tout le registre : il s'affiche après coup
   * plutôt que de retenir la fiche derrière un spinner.
   */
  const countAccounts = useCallback(async () => {
    if (!api) return;
    const snap = gate.snapshot();
    const collected: Array<StaffAccess & {id: string}> = [];
    let cursor: string | undefined;
    const seen = new Set<string>();
    try {
      for (;;) {
        const page = await api.users(cursor, snap.signal);
        collected.push(...page.items);
        if (!page.nextCursor || seen.has(page.nextCursor)) break;
        seen.add(page.nextCursor);
        cursor = page.nextCursor;
      }
      if (gate.accepts(snap.generation)) setUsers(collected);
    } catch {
      // Un décompte indisponible n'invalide ni la liste ni la fiche.
      if (gate.accepts(snap.generation)) setUsers(null);
    }
  }, [api, gate]);

  useEffect(() => { void load(); void countAccounts(); }, [dataEpoch, load, countAccounts]);

  const selectProfile = useCallback(async (profile: ProfileHead) => {
    if (!api) return;
    setSelected(profile); setCreating(null); setDetailLoading(true); setError('');
    const snap = gate.snapshot();
    try {
      const page = await api.profileVersions(profile.profileId, undefined, snap.signal);
      if (gate.accepts(snap.generation)) setVersions([...page.items].sort((a, b) => a.version - b.version));
    } catch (caught) { if (gate.accepts(snap.generation)) setError(errorMessage(caught)); }
    finally { if (gate.accepts(snap.generation)) setDetailLoading(false); }
  }, [api, gate]);

  const canCreate = !!me?.effectivePermissions.some(grant => grant.permission === 'iam.profiles.create');
  const ready = !!capacity?.ready;
  const latest = versions.at(-1);
  const accountsFor = (profileId: string) => users?.filter(user => user.assignments.some(assignment => assignment.profileId === profileId)) ?? null;

  return <div className="two">
    <section className={listLoading && !profiles.length ? 'panel is-loading' : 'panel'}>
      <div className="panel-head"><h2>Profils</h2>
        {canCreate && <button type="button" className="btn sm" disabled={!ready} title={ready ? undefined : readOnlyReason} onClick={() => { setSelected(null); setVersions([]); setCreating({mode: 'profile'}); }}><Icon name="plus" size={13} /> Nouveau profil</button>}
      </div>
      {error && <Notice tone="danger">{error}</Notice>}
      {listLoading && !profiles.length ? <Spinner /> : profiles.length ? <ul className="list plist" role="listbox" aria-label="Profils">
        {profiles.map(profile => {
          const accounts = accountsFor(profile.profileId);
          return <li key={profile.profileId} role="presentation">
            <button type="button" role="option" aria-selected={selected?.profileId === profile.profileId} className={selected?.profileId === profile.profileId ? 'row sel' : 'row'} onClick={() => void selectProfile(profile)}>
              <div className="ident-text"><strong>{profile.name}</strong><span className="uid"><code>{profile.profileId}</code></span></div>
              <span className="cell">{accounts === null ? 'calcul…' : `${accounts.length} compte${accounts.length > 1 ? 's' : ''}`}</span>
              <span className="chip ver">v{profile.latestVersion}</span>
            </button>
          </li>;
        })}
      </ul> : <Empty>Aucun profil.</Empty>}
      <div className="list-foot">
        {nextProfileCursor && <button type="button" className="btn" disabled={listLoading} onClick={async () => {
          if (!api) return; const snap = gate.snapshot(); setListLoading(true);
          try { const page = await api.profiles(nextProfileCursor, snap.signal); if (gate.accepts(snap.generation)) { setProfiles(current => [...current, ...page.items]); setNextProfileCursor(page.nextCursor); } }
          catch (caught) { if (gate.accepts(snap.generation)) setError(errorMessage(caught)); }
          finally { if (gate.accepts(snap.generation)) setListLoading(false); }
        }}>Charger la page suivante</button>}
        <p className="order-note">Ordre par identifiant, pas par date · {profiles.length} profil{profiles.length > 1 ? 's' : ''}</p>
      </div>
    </section>

    <section className={detailLoading ? 'panel detail is-loading' : 'panel detail'}>
      {creating && catalogue ? <ProfileCreator
          catalogue={catalogue}
          creation={creating}
          onCancel={() => setCreating(null)}
          onCreated={async head => { setCreating(null); await load(); await selectProfile(head); void countAccounts(); }}
        />
        : selected ? <ProfileDetail
            profile={selected}
            versions={versions}
            loading={detailLoading}
            catalogue={catalogue}
            accounts={accountsFor(selected.profileId)}
            canCreate={canCreate}
            ready={ready}
            onNewVersion={() => setCreating({mode: 'version', head: selected, base: latest?.permissions || []})}
          />
        : <Empty>Sélectionnez un profil pour consulter ses versions et ses permissions.</Empty>}
    </section>
  </div>;
}

function ProfileDetail({profile, versions, loading, catalogue, accounts, canCreate, ready, onNewVersion}: {
  profile: ProfileHead; versions: ProfileVersion[]; loading: boolean; catalogue: Catalogue | null;
  accounts: Array<StaffAccess & {id: string}> | null; canCreate: boolean; ready: boolean; onNewVersion(): void;
}) {
  const definitions = catalogue?.permissions || [];
  const latest = versions.at(-1);
  const previous = versions.at(-2);
  const diff = latest ? permissionDiff(previous?.permissions || [], latest.permissions) : {added: [], removed: []};
  const rows = useMemo(() => permissionSetRows(latest?.permissions || [], definitions), [latest, definitions]);
  const identityOf = (uid: string): Identity => cachedIdentity(uid) || unresolvedIdentity(uid);

  return <>
    <header className="detail-head">
      <div className="ident-text">
        <strong className="h2">{profile.name}</strong>
        <span className="uid"><code>{profile.profileId}</code><CopyCode value={profile.profileId} label="l’identifiant du profil" /></span>
      </div>
      <div className="head-right">
        <span className="chip ver">v{profile.latestVersion} · dernière</span>
        {canCreate && <button type="button" className="btn" disabled={!ready} title={ready ? undefined : readOnlyReason} onClick={onNewVersion}>Nouvelle version</button>}
      </div>
    </header>
    {loading ? <Spinner label="Chargement des versions…" /> : <>
      <dl className="facts">
        <div><dt>Versions</dt><dd>{versions.length}</dd></div>
        <div><dt>Comptes</dt><dd>{accounts === null ? 'calcul…' : accounts.length}</dd></div>
        <div><dt>Permissions</dt><dd>{latest?.permissions.length ?? 0}{definitions.length ? ` sur ${definitions.length} du catalogue` : ''}</dd></div>
        <div><dt>Sensibles</dt><dd>{countSensitive(latest?.permissions || [], definitions)}</dd></div>
      </dl>

      <section className="block">
        <h3>Versions</h3>
        <ul className="cards">{[...versions].reverse().map(version => <li key={version.version}>
          <strong>{version.version === latest?.version ? 'Version courante' : `Version v${version.version}`} · {version.permissions.length} permission{version.permissions.length > 1 ? 's' : ''}</strong>
          <span className="sub">v{version.version} · {version.name}</span>
        </li>)}</ul>
        <p className="muted small">Créer une version ne change aucune affectation : chaque compte garde sa version jusqu’à un changement aperçu puis confirmé.</p>
        {previous && <p className="muted small">
          Depuis la v{previous.version} : {diff.added.length ? `${diff.added.length} ajoutée${diff.added.length > 1 ? 's' : ''} (${diff.added.join(', ')})` : 'aucune ajoutée'} ·
          {' '}{diff.removed.length ? `${diff.removed.length} retirée${diff.removed.length > 1 ? 's' : ''} (${diff.removed.join(', ')})` : 'aucune retirée'}
        </p>}
      </section>

      <section className="block">
        <h3>Permissions de la v{latest?.version ?? profile.latestVersion} <span className="muted">{latest?.permissions.length ?? 0}{catalogue ? ` · catalogue v${catalogue.schemaVersion}` : ''}</span></h3>
        {rows.length ? <PermissionTable rows={rows} /> : <Empty>Cette version ne contient aucune permission.</Empty>}
      </section>

      <section className="block">
        <h3>Comptes utilisant ce profil <span className="muted">{accounts === null ? 'calcul…' : accounts.length}</span></h3>
        {accounts === null ? <p className="muted small">Décompte en cours sur l’ensemble des comptes staff.</p>
          : accounts.length ? <ul className="cards">{accounts.map(user => <li key={user.uid}>
              <IdentityCell identity={identityOf(user.uid)} />
              <span className="chip ver">{user.assignments.filter(item => item.profileId === profile.profileId).map(item => `v${item.profileVersion}`).join(', ')}</span>
            </li>)}</ul>
          : <Empty>Aucun compte n’utilise ce profil.</Empty>}
      </section>

      <AuthorBoundary />
    </>}
  </>;
}

function ProfileCreator({catalogue, creation, onCancel, onCreated}: {
  catalogue: Catalogue; creation: Creation; onCancel(): void; onCreated(head: ProfileHead): Promise<void>;
}) {
  const {api, gate, refreshAccess} = useAuth();
  const capacity = useCapacity();
  const head = creation.mode === 'version' ? creation.head : null;
  const [profileId, setProfileId] = useState(head?.profileId || '');
  const [name, setName] = useState(head?.name || '');
  const [permissions, setPermissions] = useState<string[]>(creation.mode === 'version' ? creation.base : []);
  const [reason, setReason] = useState('');
  const [receipt, setReceipt] = useState<ProfileReceipt | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const requestRef = useRef<{signature: string; requestId: string} | null>(null);
  const mutationReady = !!capacity?.ready;
  const version = (head?.latestVersion || 0) + 1;
  const selected = useMemo(() => new Set(permissions), [permissions]);
  const rows = useMemo(() => catalogueRows(catalogue.permissions), [catalogue]);

  if (receipt) return <div className="creator">
    <Notice tone="success"><strong>Version créée par le serveur.</strong> {receipt.profileId} v{receipt.version}, {formatDate(receipt.appliedAt)}. Reçu <code>{receipt.auditId}</code><CopyCode value={receipt.auditId} label="le reçu" /></Notice>
    <button type="button" className="btn primary" onClick={() => void onCreated({id: receipt.profileId, profileId: receipt.profileId, name, latestVersion: receipt.version})}>Voir le profil</button>
  </div>;

  return <form className="creator" onSubmit={async event => {
    event.preventDefault(); if (!api) return;
    setBusy(true); setError(''); const snap = gate.snapshot();
    try {
      const profile = {schemaVersion: 1 as const, profileId, version, name, permissions};
      const signature = JSON.stringify({profile, reason: reason.trim()});
      if (requestRef.current?.signature !== signature) requestRef.current = {signature, requestId: crypto.randomUUID()};
      const result = await api.createProfile(profile, reason, requestRef.current.requestId, snap.signal);
      if (gate.accepts(snap.generation)) setReceipt(result);
    } catch (caught) {
      if (gate.accepts(snap.generation)) setError(errorMessage(caught));
      if (caught instanceof ApiError && ['reauth_required', 'mfa_required', 'verified_email_required'].includes(caught.code)) await refreshAccess();
    }
    finally { if (gate.accepts(snap.generation)) setBusy(false); }
  }}>
    <div className="section-title">
      <h2>{head ? `Nouvelle version de ${head.name}` : 'Nouveau profil'}</h2>
      <button type="button" className="text-button" onClick={onCancel}>Annuler</button>
    </div>
    {error && <Notice tone="danger">{error}</Notice>}
    <div className="form-grid">
      <label>{head ? 'Profil' : 'Identifiant du nouveau profil'}<input value={profileId} onChange={event => setProfileId(event.target.value)} pattern="[A-Za-z0-9_-]{1,128}" readOnly={!!head} required /></label>
      <label>Version<input value={version} disabled /></label>
      <label>Nom<input value={name} onChange={event => setName(event.target.value)} maxLength={200} required /></label>
    </div>
    <p className="field-help">{head ? `Permissions reprises de la v${head.latestVersion}. ` : ''}Une nouvelle version ne change aucune affectation : aucun compte ne la reçoit automatiquement.</p>
    <h3>Permissions <span className="muted">{permissions.length} sur {catalogue.permissions.length} · catalogue v{catalogue.schemaVersion}</span></h3>
    <PermissionTable rows={rows} selection={{selected, onToggle: (permission, checked) => setPermissions(current => checked ? [...current, permission] : current.filter(value => value !== permission)), disabled: busy}} />
    <label>Motif de création<textarea minLength={3} maxLength={1000} value={reason} onChange={event => setReason(event.target.value)} required /></label>
    <button className="btn primary" disabled={busy || !mutationReady || !permissions.length}>{busy ? 'Création…' : `Créer ${profileId || 'le profil'} v${version}`}</button>
    {!mutationReady && <p className="field-help">{readOnlyReason}</p>}
  </form>;
}
